import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import {
  computeEstimate, DEFAULT_PRICING,
  type EstimateResult, type PricingSettings,
} from "./estimates";
import { checkHoneypot, checkFillTime, rateLimit } from "./spam-protection";

const colorSchema = z.object({
  id: z.string(),
  name: z.string(),
  number: z.string().optional(),
  hex: z.string(),
  surface: z.string().optional(),
}).passthrough();

const projectTypeEnum = z.enum([
  "interior","exterior","single_room","multiple_rooms","cabinets","trim","doors","fence","deck","garage","commercial",
]);
const surfaceEnum = z.enum(["walls","ceiling","trim","doors","windows","baseboards","crown","cabinets"]);
const conditionEnum = z.enum(["excellent","good","fair","poor","heavy"]);
const qualityEnum = z.enum(["builder","premium","luxury"]);

const roomSpecSchema = z.object({
  type: z.string().max(80).optional(),
  length: z.number().min(0).max(500).nullable().optional(),
  width: z.number().min(0).max(500).nullable().optional(),
  ceilingHeight: z.number().min(7).max(30).nullable().optional(),
  floorSqft: z.number().min(0).max(100000).nullable().optional(),
  doorCount: z.number().int().min(0).max(50).nullable().optional(),
  windowCount: z.number().int().min(0).max(50).nullable().optional(),
});

const submitSchema = z.object({
  name: z.string().trim().min(1).max(120),
  email: z.string().trim().email().max(255),
  phone: z.string().trim().min(7).max(40),
  address: z.string().trim().max(255).optional().nullable(),
  city: z.string().trim().max(120).optional().nullable(),

  projectType: projectTypeEnum,
  rooms: z.array(roomSpecSchema).max(40).default([]),
  surfaces: z.array(surfaceEnum).max(12).default([]),

  cabinetDoorDrawerCount: z.number().int().min(0).max(500).nullable().optional(),
  trimLinearFeet: z.number().int().min(0).max(10000).nullable().optional(),
  doorCount: z.number().int().min(0).max(500).nullable().optional(),
  fenceLinearFeet: z.number().int().min(0).max(10000).nullable().optional(),
  deckSqft: z.number().int().min(0).max(10000).nullable().optional(),
  exteriorSqft: z.number().int().min(0).max(100000).nullable().optional(),
  garageSqft: z.number().int().min(0).max(10000).nullable().optional(),
  commercialSqft: z.number().int().min(0).max(1000000).nullable().optional(),

  surfaceCondition: conditionEnum.optional(),
  repairsNeeded: z.string().max(2000).optional().nullable(),
  numberOfCoats: z.union([z.literal(1), z.literal(2), z.literal(3)]).default(2),
  paintQuality: qualityEnum.default("premium"),
  timeline: z.string().max(120).optional().nullable(),
  notes: z.string().max(2000).optional().nullable(),

  studioProjectId: z.string().uuid().optional().nullable(),
  studioVersionId: z.string().uuid().optional().nullable(),
  designImageUrl: z.string().url().optional().nullable(),
  selectedColors: z.array(colorSchema).max(40).default([]),
  favoriteColors: z.array(colorSchema).max(80).default([]),

  anonToken: z.string().optional().nullable(),

  honeypot: z.string().max(200).optional().nullable(),
  formStartedAt: z.number().int().nonnegative().optional().nullable(),
  turnstileToken: z.string().max(4000).optional().nullable(),
});

// Optional per-section overrides. When present, `sections` describes multiple
// distinct scopes (e.g. Interior + Exterior) that are priced individually and
// summed. The top-level fields still describe the FIRST section for legacy
// column compatibility.
const sectionSchema = z.object({
  projectType: projectTypeEnum,
  rooms: z.array(roomSpecSchema).max(40).default([]),
  surfaces: z.array(surfaceEnum).max(12).default([]),
  cabinetDoorDrawerCount: z.number().int().min(0).max(500).nullable().optional(),
  trimLinearFeet: z.number().int().min(0).max(10000).nullable().optional(),
  doorCount: z.number().int().min(0).max(500).nullable().optional(),
  fenceLinearFeet: z.number().int().min(0).max(10000).nullable().optional(),
  deckSqft: z.number().int().min(0).max(10000).nullable().optional(),
  exteriorSqft: z.number().int().min(0).max(100000).nullable().optional(),
  garageSqft: z.number().int().min(0).max(10000).nullable().optional(),
  commercialSqft: z.number().int().min(0).max(1000000).nullable().optional(),
  surfaceCondition: conditionEnum.optional(),
  repairsNeeded: z.string().max(2000).optional().nullable(),
  numberOfCoats: z.union([z.literal(1), z.literal(2), z.literal(3)]).default(2),
  paintQuality: qualityEnum.default("premium"),
});
type SectionInput = z.infer<typeof sectionSchema>;
const submitSchemaWithSections = submitSchema.extend({
  sections: z.array(sectionSchema).max(6).optional(),
});

export type EstimateSubmission = z.infer<typeof submitSchema> & {
  computed: EstimateResult;
};

async function loadPricing(): Promise<PricingSettings> {
  try {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data } = await supabaseAdmin.from("pricing_settings").select("*").limit(1).maybeSingle();
    if (!data) return DEFAULT_PRICING;
    const numeric: Partial<PricingSettings> = {};
    for (const [k, v] of Object.entries(data as Record<string, unknown>)) {
      if (typeof v === "number") (numeric as Record<string, number>)[k] = v;
      else if (typeof v === "string" && !Number.isNaN(parseFloat(v))) (numeric as Record<string, number>)[k] = parseFloat(v);
    }
    return { ...DEFAULT_PRICING, ...numeric };
  } catch {
    return DEFAULT_PRICING;
  }
}

function toEstimateInput(d: z.infer<typeof submitSchema> | Partial<z.infer<typeof submitSchema>>) {
  return {
    projectType: (d.projectType ?? "interior") as ReturnType<typeof projectTypeEnum.parse>,
    rooms: d.rooms ?? [],
    surfaces: d.surfaces ?? [],
    cabinetDoorDrawerCount: d.cabinetDoorDrawerCount ?? null,
    trimLinearFeet: d.trimLinearFeet ?? null,
    doorCount: d.doorCount ?? null,
    fenceLinearFeet: d.fenceLinearFeet ?? null,
    deckSqft: d.deckSqft ?? null,
    exteriorSqft: d.exteriorSqft ?? null,
    garageSqft: d.garageSqft ?? null,
    commercialSqft: d.commercialSqft ?? null,
    surfaceCondition: d.surfaceCondition,
    numberOfCoats: (d.numberOfCoats ?? 2) as 1 | 2 | 3,
    paintQuality: d.paintQuality ?? "premium",
  };
}

function sectionToEstimateInput(s: SectionInput) {
  return {
    projectType: s.projectType,
    rooms: s.rooms ?? [],
    surfaces: s.surfaces ?? [],
    cabinetDoorDrawerCount: s.cabinetDoorDrawerCount ?? null,
    trimLinearFeet: s.trimLinearFeet ?? null,
    doorCount: s.doorCount ?? null,
    fenceLinearFeet: s.fenceLinearFeet ?? null,
    deckSqft: s.deckSqft ?? null,
    exteriorSqft: s.exteriorSqft ?? null,
    garageSqft: s.garageSqft ?? null,
    commercialSqft: s.commercialSqft ?? null,
    surfaceCondition: s.surfaceCondition,
    numberOfCoats: (s.numberOfCoats ?? 2) as 1 | 2 | 3,
    paintQuality: s.paintQuality ?? "premium",
  };
}

function sumResults(parts: Array<{ label: string; r: EstimateResult }>): EstimateResult {
  if (parts.length === 0) {
    return {
      priceLow: 0, priceHigh: 0, gallons: 0, laborHours: 0, durationDays: 0,
      lineItems: [], assumptions: "",
      paintableWallSqft: 0, paintableCeilingSqft: 0, surfacesIncluded: [],
    };
  }
  if (parts.length === 1) return parts[0].r;
  const lineItems = parts.flatMap(({ label, r }) => r.lineItems.map((li) => ({ ...li, label: `${label} · ${li.label}` })));
  const surfaceSet = new Set<string>();
  parts.forEach(({ r }) => (r.surfacesIncluded ?? []).forEach((s) => surfaceSet.add(s)));
  return {
    priceLow: parts.reduce((s, { r }) => s + r.priceLow, 0),
    priceHigh: parts.reduce((s, { r }) => s + r.priceHigh, 0),
    gallons: Math.round(parts.reduce((s, { r }) => s + r.gallons, 0) * 10) / 10,
    laborHours: Math.round(parts.reduce((s, { r }) => s + r.laborHours, 0)),
    durationDays: Math.max(1, Math.ceil(parts.reduce((s, { r }) => s + r.durationDays, 0))),
    lineItems,
    assumptions: parts.map(({ label, r }) => `${label}: ${r.assumptions}`).join(" "),
    paintableWallSqft: Math.round(parts.reduce((s, { r }) => s + (r.paintableWallSqft ?? 0), 0)),
    paintableCeilingSqft: Math.round(parts.reduce((s, { r }) => s + (r.paintableCeilingSqft ?? 0), 0)),
    surfacesIncluded: Array.from(surfaceSet) as EstimateResult["surfacesIncluded"],
  };
}

export const previewEstimate = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) => submitSchemaWithSections.partial({
    name: true, email: true, phone: true,
  }).parse(input))
  .handler(async ({ data }) => {
    const pricing = await loadPricing();
    if (data.sections && data.sections.length > 0) {
      const parts = data.sections.map((sec) => ({
        label: String(sec.projectType).replace(/_/g, " "),
        r: computeEstimate(sectionToEstimateInput(sec), pricing),
      }));
      return sumResults(parts);
    }
    return computeEstimate(toEstimateInput(data), pricing);
  });

export const submitEstimate = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) => submitSchemaWithSections.parse(input))
  .handler(async ({ data }) => {
    const hp = checkHoneypot(data.honeypot);
    if (!hp.ok) {
      return { id: "00000000-0000-0000-0000-000000000000", computed: computeEstimate(toEstimateInput(data)) };
    }
    const ft = checkFillTime(data.formStartedAt);
    if (!ft.ok) {
      throw new Error("Your form was submitted too quickly. Please try again.");
    }

    const { getClientIp } = await import("./spam-protection.server");
    const ip = getClientIp();
    const emailKey = data.email.toLowerCase().trim();
    const ipLimit = rateLimit(`submit:ip:${ip}`, { limit: 8, windowMs: 60 * 60 * 1000 });
    if (!ipLimit.ok) {
      throw new Error(`Too many submissions from your network. Please try again in ${Math.ceil(ipLimit.retryAfterSec / 60)} minute(s) or call us at (608) 886-4598.`);
    }
    const emailLimit = rateLimit(`submit:email:${emailKey}`, { limit: 3, windowMs: 10 * 60 * 1000 });
    if (!emailLimit.ok) {
      throw new Error(`We've already received a request from this email recently. Please wait ${Math.ceil(emailLimit.retryAfterSec / 60)} minute(s) or call (608) 886-4598.`);
    }

    const pricing = await loadPricing();
    const hasSections = !!(data.sections && data.sections.length > 0);
    const sectionResults = hasSections
      ? data.sections!.map((sec) => ({
          label: String(sec.projectType).replace(/_/g, " "),
          r: computeEstimate(sectionToEstimateInput(sec), pricing),
        }))
      : [];
    const computed = hasSections
      ? sumResults(sectionResults)
      : computeEstimate(toEstimateInput(data), pricing);

    // Legacy column compatibility: derive square_footage / ceiling_height from rooms.
    const sumFloor = (data.rooms ?? []).reduce((s, r) => {
      const f = r.floorSqft && r.floorSqft > 0 ? r.floorSqft : (r.length ?? 0) * (r.width ?? 0);
      return s + (f || 0);
    }, 0);
    const avgCeil = (() => {
      const vals = (data.rooms ?? []).map((r) => r.ceilingHeight).filter((v): v is number => typeof v === "number");
      if (!vals.length) return null;
      return Math.round((vals.reduce((a, b) => a + b, 0) / vals.length) * 10) / 10;
    })();
    const surfaces = data.surfaces ?? [];

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: row, error } = await supabaseAdmin
      .from("estimate_requests")
      .insert({
        name: data.name,
        email: data.email,
        phone: data.phone,
        address: data.address ?? null,
        city: data.city ?? null,
        project_type: data.projectType,
        rooms: (data.rooms ?? []) as unknown as never,
        exterior_surfaces: [] as unknown as never,
        square_footage: sumFloor || null,
        ceiling_height: avgCeil,
        surface_condition: data.surfaceCondition ?? null,
        repairs_needed: data.repairsNeeded ?? null,
        includes_trim: surfaces.includes("trim"),
        includes_doors: surfaces.includes("doors"),
        includes_cabinets: surfaces.includes("cabinets"),
        includes_ceilings: surfaces.includes("ceiling"),
        number_of_coats: data.numberOfCoats,
        paint_quality: data.paintQuality,
        timeline: data.timeline ?? null,
        notes: data.notes ?? null,
        studio_project_id: data.studioProjectId ?? null,
        studio_version_id: data.studioVersionId ?? null,
        design_image_url: data.designImageUrl ?? null,
        selected_colors: data.selectedColors as unknown as never,
        favorite_colors: data.favoriteColors as unknown as never,
        details: {
          surfaces,
          cabinetDoorDrawerCount: data.cabinetDoorDrawerCount ?? null,
          trimLinearFeet: data.trimLinearFeet ?? null,
          doorCount: data.doorCount ?? null,
          fenceLinearFeet: data.fenceLinearFeet ?? null,
          deckSqft: data.deckSqft ?? null,
          exteriorSqft: data.exteriorSqft ?? null,
          garageSqft: data.garageSqft ?? null,
          commercialSqft: data.commercialSqft ?? null,
          lineItems: computed.lineItems,
          sections: hasSections
            ? data.sections!.map((sec, i) => ({
                ...sec,
                computed: sectionResults[i]?.r ?? null,
              }))
            : null,
        } as unknown as never,
        estimated_gallons: computed.gallons,
        estimated_labor_hours: computed.laborHours,
        estimated_duration_days: computed.durationDays,
        price_low: computed.priceLow,
        price_high: computed.priceHigh,
        estimate_assumptions: computed.assumptions,
        anon_token: data.anonToken ?? null,
      })
      .select("id")
      .single();

    if (error) {
      try {
        const { raiseAlert } = await import("@/lib/maintenance/alerts.server");
        await raiseAlert({
          severity: "critical",
          source: "estimate_submission",
          key: `estimate_submission:db_insert`,
          message: `Failed to insert estimate request: ${error.message}`,
          route: "submitEstimate",
          metadata: { project_type: data.projectType },
        });
      } catch {}
      throw new Error(error.message);
    }

    // Await notifications: fire-and-forget promises get cancelled by the
    // Workers runtime once the response resolves, so notifications must
    // complete before we return. Wrapped in try/catch so email failures
    // never block the estimate submission from succeeding.
    try {
      const { notifyAdminNewLead, notifyCustomerConfirmation } = await import("./notifications");
      const payload = {
        id: row.id,
        name: data.name,
        email: data.email,
        phone: data.phone,
        city: data.city ?? null,
        projectType: data.projectType,
        priceLow: computed.priceLow,
        priceHigh: computed.priceHigh,
        notes: data.notes ?? null,
      };
      await Promise.all([
        notifyAdminNewLead(payload),
        notifyCustomerConfirmation(payload),
      ]);
    } catch (e) {
      console.error("[submitEstimate] notification dispatch failed", e);
      try {
        const { raiseAlert } = await import("@/lib/maintenance/alerts.server");
        await raiseAlert({
          severity: "warning",
          source: "email_delivery",
          key: "email_delivery:estimate_lead",
          message: `Lead notification email failed: ${(e as Error).message}`,
          route: "submitEstimate",
          metadata: { estimate_id: row.id },
        });
      } catch {}
    }

    // Upsert into CRM contacts + log a communication event so the lead
    // appears in the general Contacts / Pipeline views alongside
    // manually-added contacts. Awaited (with try/catch) because
    // fire-and-forget promises are cancelled by the Workers runtime once
    // the response resolves, causing silent CRM sync failures.
    try {
        const emailLc = data.email.trim().toLowerCase();
        const projectTypeLabel = String(data.projectType).replace(/_/g, " ");
        const { data: existing } = await supabaseAdmin
          .from("crm_contacts")
          .select("id, pipeline_stage, source")
          .ilike("email", emailLc)
          .maybeSingle();

        let contactId: string | undefined = existing?.id;
        if (existing) {
          // Only advance stage if still upstream of "estimate"; never regress
          // a contact already progressed to proposal/won/etc.
          const upstream = existing.pipeline_stage == null
            || existing.pipeline_stage === "new"
            || existing.pipeline_stage === "contacted";
          await supabaseAdmin
            .from("crm_contacts")
            .update({
              name: data.name,
              phone: data.phone,
              address: data.address ?? null,
              city: data.city ?? null,
              last_activity_at: new Date().toISOString(),
              ...(upstream ? { pipeline_stage: "estimate" } : {}),
            })
            .eq("id", existing.id);
        } else {
          const { data: created } = await supabaseAdmin
            .from("crm_contacts")
            .insert({
              email: emailLc,
              name: data.name,
              phone: data.phone,
              address: data.address ?? null,
              city: data.city ?? null,
              source: data.studioProjectId || data.designImageUrl ? "studio_estimate" : "instant_estimate",
              pipeline_stage: "estimate",
              last_activity_at: new Date().toISOString(),
            })
            .select("id")
            .single();
          contactId = created?.id;
        }

        if (contactId) {
          await supabaseAdmin.from("crm_communications").insert({
            contact_id: contactId,
            kind: "form",
            direction: "inbound",
            subject: `Instant Estimate submitted — ${projectTypeLabel}`,
            body:
              `Estimate range: $${computed.priceLow.toLocaleString()} – $${computed.priceHigh.toLocaleString()}` +
              (data.notes ? `\n\nNotes: ${data.notes}` : ""),
            metadata: {
              estimate_id: row.id,
              project_type: data.projectType,
              price_low: computed.priceLow,
              price_high: computed.priceHigh,
              from_studio: !!(data.studioProjectId || data.designImageUrl),
            } as unknown as never,
          });
        }
    } catch (e) {
      console.error("[submitEstimate] CRM upsert failed", e);
    }

    // Sync an Opportunity so the pipeline reflects potential deal value.
    try {
      const { upsertOpportunityFromEstimate } = await import("@/lib/crm/opportunities.server");
      await upsertOpportunityFromEstimate(supabaseAdmin, {
        id: row.id,
        email: data.email,
        project_type: String(data.projectType),
        price_low: computed.priceLow,
        price_high: computed.priceHigh,
      });
    } catch (e) {
      console.error("[submitEstimate] Opportunity sync failed", e);
    }

    return { id: row.id, computed };
  });

// ---- Pricing settings admin ----

const pricingPatchSchema = z.record(z.string(), z.number()).refine((v) => Object.keys(v).length > 0);

export const getPricingSettings = createServerFn({ method: "GET" }).handler(async () => {
  return loadPricing();
});

export const updatePricingSettings = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => pricingPatchSchema.parse(input))
  .handler(async ({ data, context }) => {
    const { data: isAdmin } = await context.supabase.rpc("has_role", {
      _user_id: context.userId, _role: "admin",
    });
    if (!isAdmin) throw new Error("Forbidden");

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: existing } = await supabaseAdmin.from("pricing_settings").select("id").limit(1).maybeSingle();
    const patch = data as unknown as never;
    if (!existing) {
      const { error } = await supabaseAdmin.from("pricing_settings").insert({ label: "default", ...(data as Record<string, number>) } as unknown as never);
      if (error) throw new Error(error.message);
    } else {
      const { error } = await supabaseAdmin.from("pricing_settings").update(patch).eq("id", existing.id);
      if (error) throw new Error(error.message);
    }
    return { ok: true };
  });

// ---- Admin ----

export const listEstimates = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data: isAdmin } = await context.supabase.rpc("has_role", {
      _user_id: context.userId, _role: "admin",
    });
    if (!isAdmin) throw new Error("Forbidden");

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data, error } = await supabaseAdmin
      .from("estimate_requests")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(500);
    if (error) throw new Error(error.message);
    return data;
  });

export const updateEstimateStatus = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => z.object({
    id: z.string().uuid(),
    status: z.enum(["new", "contacted", "scheduled", "won", "lost"]),
    adminNotes: z.string().max(2000).optional(),
  }).parse(input))
  .handler(async ({ data, context }) => {
    const { data: isAdmin } = await context.supabase.rpc("has_role", {
      _user_id: context.userId, _role: "admin",
    });
    if (!isAdmin) throw new Error("Forbidden");

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const patch: { status: typeof data.status; admin_notes?: string } = { status: data.status };
    if (data.adminNotes !== undefined) patch.admin_notes = data.adminNotes;
    const { error } = await supabaseAdmin
      .from("estimate_requests")
      .update(patch)
      .eq("id", data.id);
    if (error) throw new Error(error.message);

    // Mirror the status change into the CRM pipeline so leads stay in sync
    // with the Contacts / Pipeline board. Fire-and-forget — never block.
    void (async () => {
      try {
        const { data: row } = await supabaseAdmin
          .from("estimate_requests")
          .select("email")
          .eq("id", data.id)
          .maybeSingle();
        const emailLc = row?.email?.trim().toLowerCase();
        if (!emailLc) return;
        const stageMap: Record<typeof data.status, string> = {
          new: "new",
          contacted: "contacted",
          scheduled: "estimate",
          won: "won",
          lost: "lost",
        };
        const stage = stageMap[data.status];
        await supabaseAdmin
          .from("crm_contacts")
          .update({ pipeline_stage: stage, last_activity_at: new Date().toISOString() })
          .ilike("email", emailLc);
      } catch (e) {
        console.error("[updateEstimateStatus] CRM stage sync failed", e);
      }
    })();

    return { ok: true };
  });